/**
 * subscriptionPresentation — the ONE pure resolver that turns a
 * `BillingStatus` into presentation flags/copy for every billing-aware
 * surface (BillingPage, SubscriptionPage, DashboardBillingWidget,
 * BillingStatusBanner, Layout nav gating).
 *
 * Why this exists (86cb30rva, plan §Phase 5): backend added
 * `subscription_type: 'comp'` (and 'free' was already silently unhandled by
 * every one of the five consumers above — zero of them had a branch for it).
 * Adding a fourth `if` to five components would repeat that exact mistake.
 * One React-free resolver, same shape as `verdictContract.ts` /
 * `dnsblHealth.ts` / `retentionClampWarning.ts`, means a new subscription
 * type is impossible to half-wire — the exhaustiveness check in the
 * `default:` arm below makes it a compile error instead of a silent blank
 * widget.
 *
 * Precedence mirrors what the five consumers did today (before this
 * change), so nothing regresses:
 *   loading -> comp (before every blocked/grace check) -> billing_blocked ->
 *   in_grace_period -> cancel_at_period_end -> type switch -> none
 *
 * comp jumps the queue ahead of billing_blocked/grace-period on purpose
 * (finding #5 in the plan): a comp org whose old Stripe subscription was
 * cancelled can still carry `billing_blocked=true` from that history, but a
 * comp grant is supposed to look like nothing needs attention — there is no
 * billing state left to interrupt about once an org is comp.
 */

import type { PendingChange, PendingUpgrade } from '../types/billing'

export type SubscriptionType = 'trial' | 'paid' | 'free' | 'comp' | 'none'
export type PlanKind = 'public_paid' | 'public_free' | 'internal'

export interface SubscriptionPresentationInput {
  /** Mirrors BillingContext's `loading` — true while the initial fetch is in flight. */
  loading: boolean
  subscription_type?: SubscriptionType
  billing_blocked?: boolean
  in_grace_period?: boolean
  cancel_at_period_end?: boolean
  has_subscription?: boolean
  had_trial?: boolean
  cancelled_at?: string | null
  days_remaining?: number
}

export type PresentationBadge = 'ok' | 'warn' | 'err' | 'info' | null

export interface SubscriptionPresentationCta {
  label: string
  to: string
}

export interface SubscriptionPresentation {
  kind: SubscriptionType | 'loading'
  /**
   * Two-level access, matching the two backend gates verbatim (do not
   * collapse these into one flag — that was tried and was a Free-tier nav
   * regression, see Layout.tsx buildNavSections):
   *
   * - `productAccess` mirrors `requireActiveSubscription` (server.go) —
   *   TRUE for trial/paid/comp AND free (Free degrades to FreeTier in
   *   `EvaluateOrgProductPaywall`, it is never Denied). Free-tier's
   *   published definition (migration 0113_free_tier.sql) is "dashboard +
   *   DNS checks only" — Forgers/DNS records/Reports must stay visible to
   *   Free.
   * - `businessTier` mirrors `requireBusinessTier` — the routes gated
   *   by it in server.go (compliance PDF, api-keys, alerts x2 — ADR 0013
   *   retired the missing-reports monitoring routes) are paid-tier-only;
   *   Free does NOT get these.
   */
  productAccess: boolean
  businessTier: boolean
  showUpgradeCta: boolean
  /**
   * NOTE (86cb30rva round 2, evaluated and deliberately NOT wired in):
   * `showPlanGrid` / `showStripePortal` / `showInvoicesTabs` /
   * `showBillingProfile` below are per-STATE booleans (e.g.
   * `showBillingProfile` is false for trial/free/none, not just comp).
   * `BillingPage` was checked against remapping its four sections
   * (plan grid, Stripe portal button, invoices/payments tabs, billing-
   * profile card) onto these individually and it does NOT — that page
   * gates all four coarsely on "comp or not" by design, and wiring the
   * granular per-state flags in would silently hide the billing-profile
   * card (and the others) from trial/free/none orgs, which is a real
   * behaviour regression, not a refactor. BillingPage instead computes
   * `presentation.kind === 'comp'` once and uses that single boolean for
   * all four sections. If you are about to wire these flags into a new
   * call site, first check whether that site actually wants
   * per-state-granular gating (rare) or comp-only gating (what every
   * existing surface wants) — do not assume "unused" means "safe to plug
   * in everywhere."
   */
  showPlanGrid: boolean
  showStripePortal: boolean
  showInvoicesTabs: boolean
  showBillingProfile: boolean
  showPaymentMethod: boolean
  showRenewalDate: boolean
  showCancelSubscription: boolean
  showPriceFigures: boolean
  badge: PresentationBadge
  headline: string
  body: string
  cta?: SubscriptionPresentationCta
}

/** Every commercial surface off, nothing to interrupt about — the comp/loading baseline. */
const NOTHING_COMMERCIAL = {
  showUpgradeCta: false,
  showPlanGrid: false,
  showStripePortal: false,
  showInvoicesTabs: false,
  showBillingProfile: false,
  showPaymentMethod: false,
  showRenewalDate: false,
  showCancelSubscription: false,
  showPriceFigures: false,
} as const

function loadingPresentation(): SubscriptionPresentation {
  return {
    kind: 'loading',
    productAccess: false,
    businessTier: false,
    ...NOTHING_COMMERCIAL,
    badge: null,
    headline: '',
    body: '',
  }
}

/**
 * Comp presentation — purely factual, per docs/ux/copy-voice.md: comp
 * surfaces have nothing to sell, so no urgency/painkiller framing.
 * `planName` defaults to 'Founders' only for display fallback; callers
 * should always pass the real `plan.name` — never hardcode "Founders" in a
 * component (a second internal plan needs no code change here).
 */
function compPresentation(planName: string): SubscriptionPresentation {
  return {
    kind: 'comp',
    productAccess: true,
    businessTier: true,
    ...NOTHING_COMMERCIAL,
    badge: 'ok',
    headline: `You're on ${planName}.`,
    body: 'Full access to every feature, with no billing, no renewal date, and no domain limit. Nothing here needs your attention.',
  }
}

function billingBlockedPresentation(
  input: SubscriptionPresentationInput
): SubscriptionPresentation {
  const cancelled = !!input.cancelled_at
  return {
    kind: (input.subscription_type ?? 'none') as SubscriptionType,
    productAccess: false,
    businessTier: false,
    ...NOTHING_COMMERCIAL,
    showUpgradeCta: true,
    showPlanGrid: true,
    badge: 'err',
    headline: cancelled ? 'Subscription Cancelled' : 'Subscription Suspended',
    body: cancelled
      ? 'Your subscription has been cancelled. Subscribe again to restore access to your domains.'
      : 'Your domains are unmonitored — a forger could be sending fake invoices from your domain right now. Restore access to find out.',
    cta: { label: 'View Plans', to: '/dashboard/billing' },
  }
}

function gracePeriodPresentation(): SubscriptionPresentation {
  return {
    kind: 'paid',
    productAccess: true,
    businessTier: true,
    ...NOTHING_COMMERCIAL,
    showStripePortal: true,
    showPaymentMethod: true,
    badge: 'warn',
    headline: 'Payment Failed',
    body: 'Your last payment failed. Please update your payment method before your grace period ends to avoid service interruption.',
    cta: { label: 'Update Payment', to: '/dashboard/subscription' },
  }
}

function cancellingPresentation(): SubscriptionPresentation {
  return {
    kind: 'paid',
    productAccess: true,
    businessTier: true,
    ...NOTHING_COMMERCIAL,
    showStripePortal: true,
    showInvoicesTabs: true,
    showBillingProfile: true,
    showRenewalDate: true,
    showPriceFigures: true,
    badge: 'warn',
    headline: 'Subscription Cancelling',
    body: "Your subscription is set to cancel at the end of the current period. You'll retain access until then.",
    cta: { label: 'Reactivate', to: '/dashboard/subscription' },
  }
}

function trialPresentation(
  input: SubscriptionPresentationInput
): SubscriptionPresentation {
  const daysRemaining = input.days_remaining ?? 0
  return {
    kind: 'trial',
    productAccess: daysRemaining > 0,
    businessTier: daysRemaining > 0,
    ...NOTHING_COMMERCIAL,
    showUpgradeCta: true,
    showPlanGrid: true,
    badge: daysRemaining > 0 ? 'info' : 'err',
    headline: daysRemaining > 0 ? 'Free Trial Active' : 'Trial Expired',
    body:
      daysRemaining > 0
        ? 'Your domains are monitored — upgrade before the trial ends to stay protected when a forger strikes.'
        : 'Your free trial has ended. Please subscribe to continue using DMARC Guardian.',
    cta: {
      label: daysRemaining > 0 ? 'Upgrade Now' : 'View Plans',
      to: '/dashboard/billing',
    },
  }
}

/**
 * Free-tier presentation — painkiller framed per the plan (docs/ux/copy-voice.md
 * still applies: this is a real pain, not invented urgency). Fixed in the same
 * pass as `comp` because both fell through every one of the five consumers
 * unhandled before this change.
 */
function freePresentation(): SubscriptionPresentation {
  return {
    kind: 'free',
    // Free degrades to FreeTier in EvaluateOrgProductPaywall — it is never
    // Denied by requireActiveSubscription, so it keeps product access
    // (Forgers/DNS records/Reports). It fails requireBusinessTier (Alerts,
    // API keys stay hidden). See the field doc comment above.
    productAccess: true,
    businessTier: false,
    ...NOTHING_COMMERCIAL,
    showUpgradeCta: true,
    showPlanGrid: true,
    badge: 'warn',
    headline: 'Your monitoring is limited',
    body: "On the Free tier you're only watching one domain — a forger sending fake invoices from any other domain of yours is invisible right now.",
    cta: { label: "See what you're missing →", to: '/dashboard/billing' },
  }
}

function paidActivePresentation(): SubscriptionPresentation {
  return {
    kind: 'paid',
    productAccess: true,
    businessTier: true,
    ...NOTHING_COMMERCIAL,
    showStripePortal: true,
    showInvoicesTabs: true,
    showBillingProfile: true,
    showRenewalDate: true,
    showCancelSubscription: true,
    showPriceFigures: true,
    badge: 'ok',
    headline: 'Subscription Active',
    body: 'Your account is in good standing and fully active.',
  }
}

function nonePresentation(
  input: SubscriptionPresentationInput
): SubscriptionPresentation {
  return {
    kind: 'none',
    productAccess: false,
    businessTier: false,
    ...NOTHING_COMMERCIAL,
    showUpgradeCta: true,
    showPlanGrid: true,
    badge: 'info',
    headline: input.had_trial
      ? 'Subscription Inactive'
      : 'No Active Subscription',
    body: input.had_trial
      ? 'Subscription inactive — subscribe to restore access.'
      : 'Subscribe to a plan to start monitoring your domains.',
    cta: { label: 'View Plans', to: '/dashboard/billing' },
  }
}

/**
 * Resolve a `BillingStatus` (plus a couple of derived fields already present
 * on it) into presentation flags/copy. `planName` is only used for the comp
 * headline — pass `plan?.name` from the caller, never a hardcoded string.
 */
export function resolvePlanPresentation(
  input: SubscriptionPresentationInput,
  planName = 'this plan'
): SubscriptionPresentation {
  if (input.loading) {
    return loadingPresentation()
  }

  const t: SubscriptionType = input.subscription_type ?? 'none'

  // comp jumps the queue ahead of billing_blocked/grace-period — see module
  // doc comment (finding #5): a comp grant clears billing_blocked server
  // side, but defensively we never want a comp org to see a payment banner
  // even if that invariant is ever violated upstream.
  if (t === 'comp') {
    return compPresentation(planName)
  }

  if (input.billing_blocked) {
    return billingBlockedPresentation(input)
  }

  if (input.in_grace_period) {
    return gracePeriodPresentation()
  }

  if (input.cancel_at_period_end) {
    return cancellingPresentation()
  }

  switch (t) {
    case 'trial':
      return trialPresentation(input)
    case 'free':
      return freePresentation()
    case 'paid':
      return paidActivePresentation()
    case 'none':
      return nonePresentation(input)
    default: {
      // Exhaustiveness check: the real fix for the whole bug class this
      // resolver exists to prevent. If a new SubscriptionType value is ever
      // added without a branch here, this line is a TypeScript compile
      // error, not a blank widget in production.
      const _never: never = t
      return nonePresentation({ ...input, subscription_type: _never })
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PendingChangeCard resolver (123kjkdhjru) — approved design.
//
// One new molecule, four variants: upgrade_pending, downgrade_scheduled,
// account_closure, grace_period. Priority: grace period, then downgrade or
// closure, then pending upgrade — payment failure is always the most
// urgent fact. Renders null when nothing is pending.
// ═══════════════════════════════════════════════════════════════════════

export type PendingChangeVariant =
  | 'upgrade_pending'
  | 'downgrade_scheduled'
  | 'account_closure'
  | 'grace_period'

export interface CapChangeWarning {
  /**
   * amber = paid-to-paid overage regime (billed, still ingested, nothing
   * dropped). red = hard-cap regime (excess not ingested, not queued, not
   * billed, not recoverable) — this backend currently only ever reaches
   * `red`, since `downgrade_to_free` is the sole downgrade destination.
   */
  severity: 'amber' | 'red'
  /** Full copy for the ConfirmModal — always rendered in full there. */
  fullCopy: string
  /** Amber only: folds into the card body as a trailing clause. */
  trailingClause?: string
  /** Red only: its own bolded line on the card, kept separate from retention-loss copy. */
  boldLine?: string
}

/**
 * Two regimes, two strings, never one that covers both (123kjkdhjru): the
 * red copy is the word-for-word inversion of the amber copy, so a customer
 * comparing two downgrade targets gets a structurally parallel contrast.
 * Regime is discriminated by `targetOverageEmailPricePer1k` being null (or
 * 0) or not — never by plan name or plan_kind.
 *
 * `targetEffectiveMonthlyEmailCap` MUST be a real value looked up from the
 * fetched plans list (`PricingPlan.monthly_email_limit` for the target
 * plan_id, resolved by the caller) or the backend-supplied
 * `PendingChange.target_effective_monthly_email_cap` — never a literal.
 * 123kjkdhjrh shipped the exact defect this guards against: a hardcoded
 * cap figure drifted from the real one and told a customer losing data the
 * wrong number, in the copy explaining why. `monthly_email_limit` is
 * genuinely NULL for the Free plan on the wire (migration 0161) — when the
 * caller has no real number (Free, or a lookup that legitimately found
 * none), pass `null`/`undefined` and this function omits the figure
 * entirely rather than guess it. A sentence without a number is honest; a
 * sentence with the wrong number is the bug that was just fixed.
 */
export function resolveCapChangeWarning(input: {
  targetPlanName: string
  targetOverageEmailPricePer1k?: number | null
  targetEffectiveMonthlyEmailCap?: number | null
  peakMonthlyEmailCount?: number | null
}): CapChangeWarning {
  const cap = input.targetEffectiveMonthlyEmailCap ?? null
  const capLabel = cap != null ? cap.toLocaleString('en-US') : null
  const peak = input.peakMonthlyEmailCount ?? 0
  const peakLabel = peak.toLocaleString('en-US')
  const isHardCap =
    input.targetOverageEmailPricePer1k == null ||
    input.targetOverageEmailPricePer1k === 0

  if (!isHardCap) {
    const over = cap != null && peak > cap
    return {
      severity: 'amber',
      fullCopy:
        cap != null
          ? `${input.targetPlanName} includes about ${capLabel} emails a month — anything over that is billed as overage. Nothing is dropped.`
          : `${input.targetPlanName} includes a monthly email allowance — anything over that is billed as overage. Nothing is dropped.`,
      trailingClause:
        cap != null
          ? over
            ? `, and your peak month (about ${peakLabel}) already runs past that — expect an overage line on your next invoice`
            : `; your peak month (about ${peakLabel}) stays comfortably under it`
          : undefined,
    }
  }

  return {
    severity: 'red',
    fullCopy:
      cap != null
        ? `${input.targetPlanName} hard-caps at about ${capLabel} emails a month (and about ${Math.round(cap / 10)} a day) — anything over that is not ingested, not queued, and not billed. It's just gone.`
        : `${input.targetPlanName} hard-caps your monthly email volume — anything over that is not ingested, not queued, and not billed. It's just gone.`,
    boldLine:
      cap != null
        ? `About ${capLabel} emails a month, hard cap — mail over that is silently dropped, never billed, never recoverable.`
        : `Mail over your new plan's cap is silently dropped, never billed, never recoverable.`,
  }
}

export interface PendingChangeCardResolveInput {
  pendingChange?: PendingChange | null
  pendingUpgrade?: PendingUpgrade | null
  inGracePeriod?: boolean
  gracePeriodEndsAt?: string | null
  /** subscription.current_period_end / billingStatus.current_period_end — the paid-period end. */
  accessEnd?: string | null
  peakMonthlyEmailCount?: number | null
  /** Only meaningful for a `downgrade_to_free` pending change. */
  targetOverageEmailPricePer1k?: number | null
  /** Real value looked up from the fetched plans list — see resolveCapChangeWarning's doc comment. Never a literal. */
  targetEffectiveMonthlyEmailCap?: number | null
}

export interface PendingChangeCardPresentation {
  variant: PendingChangeVariant
  effectiveAt?: string
  /** account_closure only: the paid-period end, when it differs from the deletion date. */
  accessEnd?: string
  targetPlanName?: string
  capWarning?: CapChangeWarning
  /** account_closure only — true when access_end and the deletion date are different calendar days. */
  showTwoDates?: boolean
  /**
   * upgrade_pending only — 'processing' | 'failed'. Must survive a page
   * reload (read from server state, never a toast) — see
   * PendingUpgrade doc comment in types/billing.ts.
   */
  status?: 'processing' | 'failed'
}

/**
 * Resolves the ONE pending-change card to render, or null when nothing is
 * pending. See module doc comment above for priority order.
 */
export function resolvePendingChangePresentation(
  input: PendingChangeCardResolveInput
): PendingChangeCardPresentation | null {
  if (input.inGracePeriod) {
    return {
      variant: 'grace_period',
      effectiveAt: input.gracePeriodEndsAt ?? undefined,
    }
  }

  if (input.pendingChange) {
    const pc = input.pendingChange
    if (pc.type === 'downgrade_to_free') {
      const targetPlanName = pc.target_plan_name ?? 'Free'
      return {
        variant: 'downgrade_scheduled',
        effectiveAt: pc.effective_at,
        targetPlanName,
        capWarning: resolveCapChangeWarning({
          targetPlanName,
          targetOverageEmailPricePer1k: input.targetOverageEmailPricePer1k,
          targetEffectiveMonthlyEmailCap:
            pc.target_effective_monthly_email_cap ??
            input.targetEffectiveMonthlyEmailCap,
          peakMonthlyEmailCount: input.peakMonthlyEmailCount,
        }),
      }
    }
    // cancel_account: pc.effective_at is the computed deletion date
    // (max(access_end, scheduled_at + 30 days) — see scheduleAccountClosure
    // doc comment in internal/api/account_handlers.go). Two dates render
    // only when they differ (123kjkdhjru) — cancelling near the end of a
    // period pushes deletion past the access end; introducing a second
    // date when there is nothing to distinguish is noise.
    const accessEnd = input.accessEnd ?? undefined
    const deletionDate = pc.effective_at
    const sameDay =
      !!accessEnd && accessEnd.slice(0, 10) === deletionDate.slice(0, 10)
    return {
      variant: 'account_closure',
      effectiveAt: deletionDate,
      accessEnd,
      showTwoDates: !!accessEnd && !sameDay,
    }
  }

  if (input.pendingUpgrade) {
    return {
      variant: 'upgrade_pending',
      targetPlanName: input.pendingUpgrade.target_plan_name,
      status: input.pendingUpgrade.status,
    }
  }

  return null
}
